import { Spinner } from "@/components/spinner/spinner";
import { DashboardRoutes } from "@/constants/routes";
import { useAuth } from "@/context/auth/useAuth";
import { useCampaignAvailability } from "@/domain/campaign";
import { featuresService } from "@/services/featuresService";
import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router";

/**
 * Gate duplo da feature Campanha: permissão (features do usuário) E flag por
 * empresa (availability). Ter só a permissão não basta — a feature está sendo
 * liberada por allowlist de company, e o backend responde 403 para quem está
 * fora dela.
 */

type CampaignGuardProps = {
  /** Feature exigida além da flag (ex.: "write:campaign:sheet-template"). */
  feature: string;
  children: ReactNode;
};

/** Versão para o menu: some silenciosamente quando o gate não passa. */
export const CampaignGuard = ({ feature, children }: CampaignGuardProps) => {
  const { featuresLoaded } = useAuth();
  const { availability } = useCampaignAvailability();

  if (!featuresLoaded) return null;
  if (!availability.enabled) return null;
  if (!featuresService.hasFeature(feature)) return null;

  return <>{children}</>;
};

type CampaignRouteProps = {
  feature: string;
  redirectTo?: string;
};

/**
 * Versão para o router: espera as features e a availability carregarem antes
 * de decidir (senão o acesso direto pela URL redirecionava antes da flag
 * chegar) e redireciona para o Dashboard quando a flag está desligada.
 */
export const CampaignRoute = ({
  feature,
  redirectTo = DashboardRoutes.DASHBOARD,
}: CampaignRouteProps) => {
  const { featuresLoaded } = useAuth();
  const { availability, isLoadingAvailability } = useCampaignAvailability();

  if (!featuresLoaded || isLoadingAvailability) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!availability.enabled || !featuresService.hasFeature(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};
